/**
 * Pinia store -- multi-select state for the products list (Phase 4.4).
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase 4.4 bulk action bar + multi-select.
 *
 * The page that owns the products list:
 *   1. Calls ``setAllIds(ids)`` whenever the visible list changes
 *      (initial load, page change, post-bulk-action reload).
 *   2. Wires checkboxes to ``toggle(id)`` / ``isSelected(id)``.
 *   3. Wires the select-all checkbox to ``selectAll()`` / ``clear()`` /
 *      reads ``allSelected``.
 *   4. Passes ``selectedIdArray`` into bulk endpoints.
 *
 * Per P4-Q9 the bulk bar operates on ALL selected products regardless
 * of visibility / draft status -- this store does not filter; the
 * caller decides what goes into ``allIds``.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useProductsListSelection = defineStore(
    'productsListSelection',
    () => {
        // Insertion-order-preserving Set. We keep selectedIds as a ref
        // wrapping a Set so Vue's reactivity tracks structural updates
        // when we replace the Set reference via clear() / selectAll() /
        // setAllIds() pruning. Per-element add/delete are wrapped in
        // helper functions that swap the Set reference -- mutating a
        // Set in place does NOT trigger reactivity in Vue 3.
        const selectedIds = ref(new Set())
        const allIds = ref([])

        // -- getters --
        const selectedCount = computed(() => selectedIds.value.size)
        const selectedIdArray = computed(() => Array.from(selectedIds.value))
        const allSelected = computed(() => {
            if (!allIds.value.length) return false
            for (const id of allIds.value) {
                if (!selectedIds.value.has(id)) return false
            }
            return true
        })

        function isSelected(id) {
            return selectedIds.value.has(id)
        }

        // -- actions --
        function _replaceSelection(nextSet) {
            // Reassigning the ref's value to a fresh Set triggers
            // reactivity. Mutating the existing Set does not.
            selectedIds.value = nextSet
        }

        function select(id) {
            if (selectedIds.value.has(id)) return
            const next = new Set(selectedIds.value)
            next.add(id)
            _replaceSelection(next)
        }

        function deselect(id) {
            if (!selectedIds.value.has(id)) return
            const next = new Set(selectedIds.value)
            next.delete(id)
            _replaceSelection(next)
        }

        function toggle(id) {
            if (selectedIds.value.has(id)) {
                deselect(id)
            } else {
                select(id)
            }
        }

        function selectAll() {
            _replaceSelection(new Set(allIds.value))
        }

        function clear() {
            _replaceSelection(new Set())
        }

        function setAllIds(ids) {
            const safeIds = Array.isArray(ids) ? ids.slice() : []
            allIds.value = safeIds
            // Prune any selected ids that are no longer in the visible
            // list (e.g. one product was deleted by another tab).
            const allowed = new Set(safeIds)
            let needsPrune = false
            for (const id of selectedIds.value) {
                if (!allowed.has(id)) {
                    needsPrune = true
                    break
                }
            }
            if (needsPrune) {
                const next = new Set()
                for (const id of selectedIds.value) {
                    if (allowed.has(id)) next.add(id)
                }
                _replaceSelection(next)
            }
        }

        return {
            // state (exposed for templates that prefer raw access)
            selectedIds,
            allIds,
            // getters
            selectedCount,
            selectedIdArray,
            allSelected,
            isSelected,
            // actions
            select,
            deselect,
            toggle,
            selectAll,
            clear,
            setAllIds,
        }
    },
)
